/**
 * PurchaseModal v2 — customer-friendly buy-report-credits popup.
 *
 * Layout guarantees:
 *   • Always resets scroll to top when the modal opens.
 *   • Shows all THREE package cards on desktop without vertical scroll —
 *     compact layout designed to fit inside a 92 vh container.
 *   • Sticky header + footer on mobile so the X + Cancel + Continue action
 *     stay in view when the body scrolls.
 *   • X (top-right), Cancel (footer), Escape key, and clicks on the dimmed
 *     overlay ALL close the modal cleanly. Body scroll position is preserved
 *     via a temporary scroll lock — the customer returns exactly where they
 *     were, no refresh required.
 *
 * Business rules:
 *   • Three package choices (single_report / credit_bundle_3 / credit_bundle_5).
 *     credit_bundle_3 is preselected on every open. "Recommended" pill on
 *     bundle_3, "Best value · Save $150" pill on bundle_5.
 *   • Uses "report credits" language consistently. No per-report math on cards.
 *   • $149 single_report requires a locked project selection — if the user
 *     picks it without a project, they see an inline hint and Continue is
 *     disabled until they pick one.
 *   • Cancel / close does NOT change project, wallet balance, or start a
 *     payment.
 */
import { useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, Sparkles, Star, FileText, Loader2, ArrowRight } from "lucide-react";
import { initializePaddle } from "@paddle/paddle-js";
import { pickPackageIcon } from "@/lib/purchaseModalIcons";
import { deriveCheckoutToast, creditsLabel } from "@/lib/purchaseModalToast";
import { recordReportVersion, recordReportVersionWhenPaid } from "@/lib/reportVersion";

// Map string identifiers from pickPackageIcon → lucide components so
// the pure helper stays test-friendly without importing lucide.
const ICON_MAP = { FileText, Star, Sparkles };
const IconFor = ({ pkg }) => {
  const name = pickPackageIcon(pkg);
  const Cmp = ICON_MAP[name] || FileText;
  const color = name === "Star" ? "#E58B25" : "var(--brand-green)";
  return <Cmp size={14} style={{ color }} />;
};
import { api } from "@/lib/api";
import { buildCheckoutPayload } from "@/lib/purchaseCheckout";
import { money } from "@/lib/numfmt";
import { getLocale } from "@/lib/i18n";

// a07c — the modal in the buyer's language. EN copy unchanged.
const L = (en, fr) => (getLocale() === "fr" ? fr : en);
const credLabel = (n) => creditsLabel(n, getLocale());

const PACKAGES = [
  {
    id: "single_report",
    package_id: "single_report",
    price_usd: 99,
    credits: 1,
    title: "Detailed report",
    title_fr: "Rapport détaillé",
    summary: "Launch price. The full digital scope: power model, equipment, budgetary costs, permitting, feasibility memo and an initial project & financial plan.",
    summary_fr: "Prix de lancement. Le périmètre numérique complet : modèle énergétique, équipements, coûts budgétaires, autorisations, mémorandum de faisabilité et plan initial projet & financier.",
    badge: { label: "Most popular", label_fr: "Le plus choisi", tone: "brand" },
    kind: "credits",
    row: 1,
  },
  {
    id: "credit_bundle_2",
    package_id: "credit_bundle_2",
    price_usd: 180,
    credits: 2,
    title: "2 reports",
    title_fr: "2 rapports",
    summary: "~$90 each · save ~9%. Report + nine-tab Excel model + comparison CSV.",
    summary_fr: "~90 $ l'unité · ~9 % d'économie. Rapport + modèle Excel à neuf onglets + CSV de comparaison.",
    kind: "credits",
    row: 1,
  },
  {
    id: "credit_bundle_3",
    package_id: "credit_bundle_3",
    price_usd: 249,
    credits: 3,
    title: "3 reports",
    title_fr: "3 rapports",
    summary: "~$83 each · save ~16%. Report + nine-tab Excel model + comparison CSV + a complimentary 30-minute engineer call.",
    summary_fr: "~83 $ l'unité · ~16 % d'économie. Rapport + modèle Excel à neuf onglets + CSV de comparaison + un appel offert de 30 minutes avec un ingénieur.",
    kind: "credits",
    row: 1,
  },
  {
    id: "credit_bundle_5",
    package_id: "credit_bundle_5",
    price_usd: 375,
    credits: 5,
    title: "5 reports",
    title_fr: "5 rapports",
    summary: "~$75 each · save ~24%. Report + nine-tab Excel model + comparison CSV + a complimentary 1-hour engineer call.",
    summary_fr: "~75 $ l'unité · ~24 % d'économie. Rapport + modèle Excel à neuf onglets + CSV de comparaison + un appel offert d'une heure avec un ingénieur.",
    badge: { label: "Best Value · Save $120", label_fr: "Meilleur rapport qualité-prix · 120 $ d'économie", tone: "accent" },
    kind: "credits",
    row: 2,
  },
  {
    id: "credit_bundle_10",
    package_id: "credit_bundle_10",
    price_usd: 650,
    credits: 10,
    title: "10 reports",
    title_fr: "10 rapports",
    summary: "~$65 each · save ~34%. Report + nine-tab Excel model + comparison CSV + a complimentary 2-hour engineer call + a development-mandate outline.",
    summary_fr: "~65 $ l'unité · ~34 % d'économie. Rapport + modèle Excel à neuf onglets + CSV de comparaison + un appel offert de 2 heures avec un ingénieur + une trame de mandat de développement.",
    kind: "credits",
    row: 2,
  },
  {
    id: "credit_bundle_20",
    package_id: "credit_bundle_20",
    price_usd: 1100,
    credits: 20,
    title: "20 reports",
    title_fr: "20 rapports",
    summary: "~$55 each · save ~44%. Report + nine-tab Excel model + comparison CSV + a complimentary 4-hour engineer call + a development-mandate outline + a pre-feasibility study pattern.",
    summary_fr: "~55 $ l'unité · ~44 % d'économie. Rapport + modèle Excel à neuf onglets + CSV de comparaison + un appel offert de 4 heures avec un ingénieur + une trame de mandat de développement + un modèle d'étude de préfaisabilité.",
    kind: "credits",
    row: 2,
  },
];

// Convenience — anything with a numeric price is live and can be selected/paid.
export const isEnabled = (p) => typeof p.price_usd === "number" && p.price_usd > 0;
export const PURCHASE_PACKAGES = PACKAGES;
const enabledPackages = PACKAGES.filter(isEnabled);

// One card. Extracted so both rows in the modal reuse the same visual style,
// including the row-2 (portfolio) cards which are intentionally badge-less.
const renderPackageCard = (p, choice, setChoice, selectedProjectId, startProjectId) => {
  const active = choice === p.id;
  const disabledInline = false;
  return (
    <button
      key={p.id}
      type="button"
      onClick={() => setChoice(p.id)}
      data-testid={`option-${p.id}`}
      aria-pressed={active}
      className={`text-left p-4 rounded-md relative transition-all ${disabledInline ? "opacity-70" : ""}`}
      style={{
        background: active ? "var(--paper)" : "var(--white)",
        border: active ? "2px solid var(--brand-green)" : "1px solid var(--hairline)",
        boxShadow: active ? "0 6px 16px -8px rgba(26,111,43,0.35)" : "none",
      }}
    >
      {p.badge && (
        <span
          className="absolute -top-2 left-3 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
          style={{
            background: p.badge.tone === "accent" ? "#E58B25" : "var(--brand-green)",
            color: "#fff",
          }}
          data-testid={`badge-${p.id}`}
        >
          {L(p.badge.label, p.badge.label_fr)}
        </span>
      )}
      <div className="flex items-baseline gap-2 mt-1">
        <IconFor pkg={p} />
        <div className="font-display text-base leading-tight" style={{ color: "var(--char)" }}>{L(p.title, p.title_fr)}</div>
      </div>
      <div className="font-display text-2xl mt-2" style={{ color: "var(--char)" }}>{money(p.price_usd)}</div>
      <div className="text-xs mt-2" style={{ color: "var(--muted)" }}>{L(p.summary, p.summary_fr)}</div>
      {active && (
        <div className="text-[11px] mt-2 font-semibold uppercase tracking-wider" style={{ color: "var(--brand-green)" }}>
          {L("Selected", "Sélectionné")}
        </div>
      )}
    </button>
  );
};

const loadPayPalSdk = () => new Promise((resolve, reject) => {
  if (window.paypal) return resolve(window.paypal);
  const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
  if (!clientId) return reject(new Error("PayPal client ID missing"));
  const s = document.createElement("script");
  s.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=USD&disable-funding=venmo`;
  s.onload = () => resolve(window.paypal);
  s.onerror = () => reject(new Error("PayPal SDK failed to load"));
  document.body.appendChild(s);
});


// ── Provider tiles ────────────────────────────────────────────────────────
// Inline branded SVG marks so the modal has no external CDN dependency.
// Deliberately compact — the tile is the interactive area, the mark is small.
const PayPalMark = ({ height = 22 }) => (
  <svg viewBox="0 0 100 26" height={height} xmlns="http://www.w3.org/2000/svg" aria-label="PayPal">
    <text x="0" y="20" fontFamily="Helvetica, Arial, sans-serif" fontWeight="700" fontStyle="italic" fontSize="22" fill="#003087">Pay</text>
    <text x="42" y="20" fontFamily="Helvetica, Arial, sans-serif" fontWeight="700" fontStyle="italic" fontSize="22" fill="#009cde">Pal</text>
  </svg>
);
const PaddleMark = ({ height = 22 }) => (
  <svg viewBox="0 0 100 26" height={height} xmlns="http://www.w3.org/2000/svg" aria-label="Paddle">
    <rect x="0" y="3" width="20" height="20" rx="4" fill="#111"/>
    <rect x="5" y="8" width="10" height="10" rx="2" fill="#FFFFFF"/>
    <text x="26" y="20" fontFamily="Helvetica, Arial, sans-serif" fontWeight="700" fontSize="20" fill="#111">Paddle</text>
  </svg>
);

const ProviderTile = ({ provider, active, onSelect }) => {
  const Mark = provider === "paypal" ? PayPalMark : PaddleMark;
  const sub = provider === "paypal"
    ? L("PayPal balance, credit or debit card", "Solde PayPal, carte de crédit ou de débit")
    : L("Cards, Apple Pay, Google Pay", "Cartes, Apple Pay, Google Pay");
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={`provider-${provider}`}
      aria-pressed={active}
      className="text-left p-4 rounded-md relative transition-all"
      style={{
        background: active ? "var(--paper)" : "var(--white)",
        border: active ? "2px solid var(--brand-green)" : "1px solid var(--hairline)",
        boxShadow: active ? "0 6px 16px -8px rgba(26,111,43,0.35)" : "none",
      }}
    >
      <div className="flex items-center gap-3">
        <div style={{ width: 90, display: "flex", alignItems: "center" }}>
          <Mark />
        </div>
        {active && (
          <span className="ml-auto text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--brand-green)" }}>
            {L("Selected", "Sélectionné")}
          </span>
        )}
      </div>
      <div className="text-xs mt-2" style={{ color: "var(--muted)" }}>{sub}</div>
    </button>
  );
};

export const PurchaseModal = ({ open, onClose, lockedProjects = [], startProjectId = null, startPackageId = null, onPurchaseComplete }) => {
  const [choice, setChoice] = useState("credit_bundle_3");
  const [selectedProjectId, setSelectedProjectId] = useState(startProjectId);
  const [busy, setBusy] = useState(false);
  const [paypalReady, setPaypalReady] = useState(false);
  // ── 3-step state machine: "package" → "provider" → "pay" ──────────────
  // package  = choosing which offer to buy (+ optional promo for $149 single)
  // provider = choosing PayPal vs Paddle, neither pre-selected
  // pay      = provider-specific checkout mounted / opened
  const [step, setStep] = useState("package");
  // Provider chosen on step="provider". Kept null until the buyer explicitly
  // picks one — Continue on the provider step is disabled until it flips.
  const [provider, setProvider] = useState(null);
  // Back-compat alias so existing code that reads `showPay` keeps working.
  const showPay = step === "pay";
  const paypalRef = useRef(null);
  const paddleRef = useRef(null);
  const paddleTxnRef = useRef(null);
  const dialogRef = useRef(null);
  const bodyRef = useRef(null);
  const savedScrollRef = useRef(0);

  // Promo state — applies to whichever package the buyer currently has selected.
  // Any 100%-off code redeemed via the modal grants that package's credits to
  // the buyer's wallet server-side; the modal simply presents the discount UX.
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);
  // `promoStatus`: null | {kind:'error', message} | {kind:'applied', code, projectId}
  const [promoStatus, setPromoStatus] = useState(null);

  const pkg = useMemo(() => PACKAGES.find((p) => p.id === choice) || PACKAGES[0], [choice]);
  const needsProject = false;

  // ─── Reset scroll + defaults on every open ───
  useEffect(() => {
    if (!open) return;
    // If caller asked to preselect a package that's currently disabled, fall
    // back to the safe default so the modal never opens with an invalid choice.
    const requested = PACKAGES.find((p) => p.id === startPackageId);
    const initial = requested && isEnabled(requested) ? requested.id : "credit_bundle_3";
    setChoice(initial);
    setStep("package");
    setProvider(null);
    setSelectedProjectId(startProjectId);
    setPromoOpen(false);
    setPromoCode("");
    setPromoStatus(null);
    // Delay one tick so the dialog is mounted, then scroll it to top.
    requestAnimationFrame(() => {
      if (bodyRef.current) bodyRef.current.scrollTop = 0;
    });
  }, [open, startProjectId, startPackageId]);

  // ─── Changing the selected package MUST invalidate the provider choice.
  // The user re-confirms the payment method the next time they hit Continue.
  const setChoiceGuarded = (nextId) => {
    if (nextId === choice) return;
    setChoice(nextId);
    setProvider(null);
    if (step !== "package") setStep("package");
  };

  // Any time the package changes, wipe promo state — a code applied to one
  // package must not silently carry over to a different one. Spec:
  // "Switching packages after applying a code should clear it and return to
  // the normal payment flow."
  useEffect(() => {
    setPromoOpen(false);
    setPromoCode("");
    setPromoStatus(null);
  }, [choice]);
  useEffect(() => {
    setPromoStatus(null);
    setPromoCode("");
    setPromoOpen(false);
  }, [selectedProjectId]);

  // ─── Scroll lock that preserves position on close ───
  useEffect(() => {
    if (!open) return;
    savedScrollRef.current = window.scrollY;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
      // Restore the exact scroll position the customer had when they opened the modal.
      window.scrollTo({ top: savedScrollRef.current, behavior: "instant" });
    };
  }, [open]);

  // ─── Escape closes ───
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") { e.preventDefault(); onClose?.(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // ─── PayPal button mounting — only when the buyer picked PayPal on the
  //     provider step and we've advanced to the pay step.
  useEffect(() => {
    if (!open || step !== "pay" || provider !== "paypal" || needsProject) return;
    const el = paypalRef.current;
    if (!el) return;
    el.innerHTML = "";
    setPaypalReady(false);
    loadPayPalSdk().then((paypal) => {
      if (!el.isConnected) return;
      paypal.Buttons({
        style: { layout: "vertical", color: "gold", shape: "rect", label: "paypal", height: 40 },
        createOrder: async () => {
          setBusy(true);
          try {
            const { data } = await api.post("/payments/paypal/create-order", {
              ...buildCheckoutPayload({
                pkg: { id: pkg.id },
                projectId: selectedProjectId,
              }),
              origin_url: window.location.origin,
            });
            return data.order_id;
          } finally {
            setBusy(false);
          }
        },
        onApprove: async (data) => {
          setBusy(true);
          try {
            const { data: cap } = await api.post(`/payments/paypal/capture/${data.orderID}`);
            if (cap.paid) {
              if (cap.project_paid === true && selectedProjectId) await recordReportVersion(selectedProjectId, "single");
              toast.success(deriveCheckoutToast({ pkgId: pkg.id, credits: pkg.credits, lang: getLocale() }));
              onPurchaseComplete && onPurchaseComplete({ orderId: data.orderID, choice });
              onClose?.();
            } else {
              toast.error(L("Payment not completed", "Paiement non finalisé"));
            }
          } catch {
            toast.error(L("Payment capture failed", "Échec de l'encaissement du paiement"));
          } finally {
            setBusy(false);
          }
        },
        onError: () => toast.error(L("Payment error — please try again", "Erreur de paiement — veuillez réessayer")),
      }).render(el).then(() => setPaypalReady(true));
    }).catch(() => toast.error(L("PayPal is unavailable right now", "PayPal est indisponible pour le moment")));
  }, [open, step, provider, needsProject, pkg, selectedProjectId, choice, onClose, onPurchaseComplete]);

  if (!open) return null;

  const summaryCredits = credLabel(pkg.credits);

  const onOverlayClick = (e) => {
    // Only fire when the dimmed area itself is clicked — not the dialog body
    if (e.target === e.currentTarget) onClose?.();
  };

  // Render into document.body via portal — the ancestor <header> uses
  // `backdrop-filter: blur(12px)` which turns it into the containing block
  // for `position: fixed`. Without the portal, `fixed inset-0` would clip to
  // the header's height (~79 px) instead of the full viewport.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6"
      style={{ background: "rgba(22,33,29,0.55)" }}
      data-testid="purchase-modal"
      onClick={onOverlayClick}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="purchase-modal-title"
        className="w-full max-w-3xl rounded-lg shadow-xl relative flex flex-col overflow-hidden"
        style={{ background: "var(--white)", maxHeight: "88vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — outside the scroll region, always visible */}
        <div
          className="flex items-start justify-between gap-4 px-6 pt-5 pb-4 shrink-0"
          style={{ background: "var(--white)", borderBottom: "1px solid var(--hairline)" }}
        >
          <div>
            <div className="overline">{L("Buy report credits", "Acheter des crédits de rapport")}</div>
            <h3 id="purchase-modal-title" className="font-display text-xl mt-1" style={{ color: "var(--char)" }}>
              {L("Choose your package", "Choisissez votre offre")}
            </h3>
          </div>
          <button
            type="button"
            onClick={() => onClose?.()}
            aria-label={L("Close", "Fermer")}
            className="p-1 rounded"
            style={{ color: "var(--muted)" }}
            data-testid="purchase-modal-close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body — only this region scrolls when content overflows */}
        <div ref={bodyRef} className="px-6 py-5 flex-1 overflow-y-auto" data-testid="purchase-modal-body">

          {/* Package cards — Row 1: single · bundle-2 · bundle-3. Row 2: bundle-5 · bundle-10 · bundle-20. */}
          <div data-testid="purchase-options">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="purchase-options-row-1">
              {PACKAGES.filter((p) => p.row === 1 && isEnabled(p)).map((p) => renderPackageCard(p, choice, setChoiceGuarded, selectedProjectId, startProjectId))}
            </div>
            {PACKAGES.some((p) => p.row === 2 && isEnabled(p)) && (
              <>
                <div className="mt-5 mb-2 text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--muted)" }} data-testid="purchase-options-row-2-label">
                  {L("Larger portfolio bundles", "Packs pour portefeuilles plus importants")}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="purchase-options-row-2">
                  {PACKAGES.filter((p) => p.row === 2 && isEnabled(p)).map((p) => renderPackageCard(p, choice, setChoiceGuarded, selectedProjectId, startProjectId))}
                </div>
              </>
            )}
          </div>

          {needsProject && (
            <div
              className="mt-3 text-sm rounded p-3"
              style={{ background: "#FDECD9", border: "1px solid #E58B25", color: "#873F00" }}
              data-testid="single-needs-project-hint"
              role="status"
            >
              {L("Please select a project above before continuing with the $149 single-report option.", "Sélectionnez un projet ci-dessus avant de poursuivre avec l'offre rapport unique à 149 $.")}
            </div>
          )}

          {/* Payment summary */}
          <div
            className="mt-5 p-4 rounded-md flex justify-between items-center flex-wrap gap-2"
            style={{ background: "var(--paper)" }}
            data-testid="purchase-summary"
          >
            <div>
              <div className="text-xs uppercase tracking-wider" style={{ color: "var(--muted)" }}>{L("You pay", "Vous payez")}</div>
              <div className="font-display text-2xl" style={{ color: "var(--char)" }} data-testid="summary-amount">
                {promoStatus?.kind === "applied" ? <span><s style={{ color: "var(--muted)" }}>{money(pkg.price_usd)}</s>{" "}<span style={{ color: "var(--brand-green)" }}>$0</span></span> : <>{money(pkg.price_usd)}</>}
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider" style={{ color: "var(--muted)" }}>{L("You get", "Vous obtenez")}</div>
              <div className="font-display text-base" style={{ color: "var(--char)" }} data-testid="summary-credits">{summaryCredits}</div>
            </div>
          </div>

          {/* Promo section — available for all 5 wallet-credit packages.
              Collapsed by default so it doesn't clutter the screen for
              customers without a code. */}
          <div className="mt-3" data-testid="promo-section">
              {!promoOpen ? (
                <button
                  type="button"
                  onClick={() => setPromoOpen(true)}
                  className="text-xs underline"
                  style={{ color: "var(--muted)" }}
                  data-testid="promo-open-link"
                >
                  {L("Have a promo code?", "Vous avez un code promo ?")}
                </button>
              ) : (
                <div className="flex flex-col gap-2" data-testid="promo-form">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoStatus(null); }}
                      placeholder={L("Promo code", "Code promo")}
                      className="flex-1 px-3 py-2 text-sm rounded"
                      style={{ background: "var(--white)", border: "1px solid var(--hairline)", letterSpacing: "0.05em", fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                      autoFocus
                      disabled={promoBusy || promoStatus?.kind === "applied"}
                      data-testid="promo-input"
                    />
                    <button
                      type="button"
                      onClick={async () => {
                        if (!promoCode.trim()) return;
                        setPromoBusy(true); setPromoStatus(null);
                        try {
                          const { data } = await api.post("/promo/redeem", {
                            code: promoCode.trim(),
                            package_id: pkg.package_id,
                          }, { params: { lang: getLocale() } });
                          setPromoStatus({
                            kind: "applied",
                            code: promoCode.trim(),
                            packageId: pkg.package_id,
                            creditsAdded: data?.credits_added || pkg.credits,
                          });
                        } catch (e) {
                          const msg = e?.response?.data?.detail || L("That code isn't valid — please double-check.", "Ce code n'est pas valable — vérifiez-le.");
                          setPromoStatus({ kind: "error", message: msg });
                        } finally { setPromoBusy(false); }
                      }}
                      disabled={promoBusy || !promoCode.trim() || promoStatus?.kind === "applied"}
                      className="btn-secondary !py-2 !px-4 text-sm"
                      data-testid="promo-apply-btn"
                    >
                      {promoBusy ? <Loader2 size={13} className="animate-spin" /> : null} {L("Apply", "Valider")}
                    </button>
                  </div>
                  {promoStatus?.kind === "error" && (
                    <div className="text-sm p-2 rounded" style={{ background: "#FDECEC", border: "1px solid #C0392B", color: "#7A1F17" }} data-testid="promo-error">
                      {promoStatus.message}
                    </div>
                  )}
                  {promoStatus?.kind === "applied" && (
                    <div className="text-sm p-2 rounded" style={{ background: "#E4F3E7", border: "1px solid var(--brand-green)", color: "#1A6F2B" }} data-testid="promo-applied">
                      {L(`Code accepted. ${credLabel(promoStatus.creditsAdded)} will be added to your wallet at no charge — no payment required.`,
                         `Code accepté. ${credLabel(promoStatus.creditsAdded)} ser${promoStatus.creditsAdded <= 1 ? "a ajouté" : "ont ajoutés"} gratuitement à votre portefeuille — aucun paiement requis.`)}
                    </div>
                  )}
                </div>
              )}
            </div>

          {/* Provider chooser — shown after Continue on the package step.
              Nothing selected by default; the primary CTA in the sticky footer
              stays disabled until `provider` flips to "paypal" or "paddle". */}
          {step === "provider" && !needsProject && (
            <div className="mt-4" data-testid="provider-slot">
              <div className="text-sm mb-3" style={{ color: "var(--muted)" }}>
                {L(`Choose how you want to pay for ${pkg.title} — `, `Choisissez votre moyen de paiement pour ${pkg.title_fr} — `)}<span className="font-semibold" style={{ color: "var(--char)" }}>{money(pkg.price_usd)}</span>.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <ProviderTile provider="paypal" active={provider === "paypal"} onSelect={() => setProvider("paypal")} />
                <ProviderTile provider="paddle" active={provider === "paddle"} onSelect={() => setProvider("paddle")} />
              </div>
              <div className="text-xs mt-3" style={{ color: "var(--muted)" }}>
                {L(`Both options charge exactly ${money(pkg.price_usd)}. A confirmed payment adds ${credLabel(pkg.credits)} to your wallet.`,
                   `Les deux options débitent exactement ${money(pkg.price_usd)}. Un paiement confirmé ajoute ${credLabel(pkg.credits)} à votre portefeuille.`)}
              </div>
            </div>
          )}

          {/* PayPal card slot — mounted only when the buyer picked PayPal AND advanced to pay */}
          {step === "pay" && provider === "paypal" && !needsProject && (
            <div className="mt-4" data-testid="paypal-slot">
              <div ref={paypalRef} data-testid="paypal-container" />
              {!paypalReady && (
                <div className="text-xs text-center mt-2 flex items-center justify-center gap-2" style={{ color: "var(--muted)" }}>
                  <Loader2 size={13} className="animate-spin" /> {L("Loading PayPal…", "Chargement de PayPal…")}
                </div>
              )}
            </div>
          )}

          {/* Paddle card slot — mounted only when the buyer picked Paddle AND advanced to pay */}
          {step === "pay" && provider === "paddle" && !needsProject && (
            <div className="mt-4" data-testid="paddle-slot">
              <button
                type="button"
                onClick={async () => {
                  if (busy) return;
                  setBusy(true);
                  try {
                    // Lazy-init Paddle the first time we need it in this modal.
                    if (!paddleRef.current) {
                      const token = process.env.REACT_APP_PADDLE_CLIENT_TOKEN;
                      const env = process.env.REACT_APP_PADDLE_ENV;
                      if (!token || !env) {
                        toast.error(L("Paddle is not configured — try PayPal.", "Paddle n'est pas configuré — essayez PayPal."));
                        return;
                      }
                      paddleRef.current = await initializePaddle({
                        token,
                        environment: env === "sandbox" ? "sandbox" : "production",
                        eventCallback: (ev) => {
                          if (ev?.name === "checkout.completed") {
                            // The Paddle webhook is authoritative — it flips
                            // project.paid or increments wallet.credits idempotently.
                            // Here we just close the modal and let the caller refresh.
                            toast.success(pkg.id === "single_report"
                              ? L("Payment received — unlocking your report…", "Paiement reçu — déblocage de votre rapport…")
                              : L(`Payment received — ${pkg.credits} credits are being added to your wallet.`, `Paiement reçu — ${credLabel(pkg.credits)} ${pkg.credits <= 1 ? "est" : "sont"} en cours d'ajout à votre portefeuille.`));
                            // The confirmation may land seconds after this event; the helper waits for it.
                            const txnId = ev?.data?.transaction_id || paddleTxnRef.current;
                            if (selectedProjectId) recordReportVersionWhenPaid(txnId, selectedProjectId, "single");
                            onPurchaseComplete && onPurchaseComplete({ orderId: ev?.data?.transaction_id, choice, provider: "paddle" });
                            onClose?.();
                          }
                        },
                      });
                    }
                    const { data } = await api.post("/payments/paddle/create-transaction", {
                      ...buildCheckoutPayload({
                        pkg: { id: pkg.id },
                        projectId: selectedProjectId,
                      }),
                      origin_url: window.location.origin,
                    });
                    paddleTxnRef.current = data.transaction_id;
                    paddleRef.current.Checkout.open({
                      transactionId: data.transaction_id,
                      settings: { displayMode: "overlay", variant: "one-page" },
                    });
                  } catch {
                    toast.error(L("Card checkout unavailable — please try PayPal.", "Paiement par carte indisponible — essayez PayPal."));
                  } finally {
                    setBusy(false);
                  }
                }}
                disabled={busy}
                className="btn-primary w-full !justify-center"
                data-testid="paddle-open-checkout"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                {L("Open Paddle checkout", "Ouvrir le paiement Paddle")} — {money(pkg.price_usd)}
              </button>
              <div className="text-xs text-center mt-2" style={{ color: "var(--muted)" }}>
                {L("Cards, Apple Pay, Google Pay", "Cartes, Apple Pay, Google Pay")}
              </div>
            </div>
          )}
        </div>

        {/* Sticky footer — step-aware controls */}
        <div
          className="px-6 py-4 flex items-center justify-between gap-3 flex-wrap sticky bottom-0"
          style={{ background: "var(--white)", borderTop: "1px solid var(--hairline)", zIndex: 2 }}
        >
          {/* Left-hand action: Cancel on package + pay steps, Back on provider step */}
          {step === "provider" ? (
            <button
              type="button"
              onClick={() => { setProvider(null); setStep("package"); }}
              className="btn-secondary !py-2 !px-4 text-sm"
              data-testid="purchase-back"
            >
              ← {L("Back", "Retour")}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onClose?.()}
              className="btn-secondary !py-2 !px-4 text-sm"
              data-testid="purchase-cancel"
            >
              {L("Cancel · Back to project", "Annuler · Retour au projet")}
            </button>
          )}
          {/* Right-hand action: primary CTA changes per step */}
          {step === "pay" ? (
            <span className="text-xs" style={{ color: "var(--muted)" }} data-testid="purchase-processing-hint">
              {busy
                ? L("Processing…", "Traitement en cours…")
                : (provider === "paypal"
                    ? L("Complete your purchase via PayPal above.", "Finalisez votre achat via PayPal ci-dessus.")
                    : L("Click ‘Open Paddle checkout’ to finish.", "Cliquez sur « Ouvrir le paiement Paddle » pour terminer."))}
            </span>
          ) : step === "provider" ? (
            <button
              type="button"
              onClick={() => { if (provider) setStep("pay"); }}
              disabled={!provider || busy}
              title={!provider ? L("Choose a payment method to continue", "Choisissez un moyen de paiement pour continuer") : L("Continue to payment", "Continuer vers le paiement")}
              className="btn-primary !py-2 !px-5 text-sm"
              data-testid="provider-continue"
            >
              {L("Continue to payment", "Continuer vers le paiement")}
              <ArrowRight size={14} />
            </button>
          ) : promoStatus?.kind === "applied" ? (
            <button
              type="button"
              onClick={async () => {
                if (busy) return;
                setBusy(true);
                try {
                  // The promo/redeem call already credited the wallet on the server.
                  // Just fire the completion callback so the parent can refresh
                  // the wallet chip / credits UI, then close the modal.
                  const added = promoStatus.creditsAdded || pkg.credits;
                  toast.success(L(`${added} report credit${added === 1 ? "" : "s"} added to your wallet with promo`, `${credLabel(added)} ajouté${added <= 1 ? "" : "s"} à votre portefeuille grâce au code promo`));
                  onPurchaseComplete && onPurchaseComplete({
                    orderId: `PROMO-${promoStatus.code}`,
                    choice,
                    provider: "promo",
                    promo: promoStatus.code,
                    creditsAdded: added,
                  });
                  onClose?.();
                } catch {
                  toast.error(L("Could not finalise the redemption — please contact support", "Impossible de finaliser l'utilisation du code — contactez le support"));
                } finally { setBusy(false); }
              }}
              className="btn-primary !py-2 !px-5 text-sm"
              disabled={busy}
              data-testid="promo-redeem-btn"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : null}
              {L("Redeem and unlock credits", "Utiliser le code et débloquer les crédits")}
              <ArrowRight size={14} />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setStep("provider")}
              disabled={needsProject}
              className="btn-primary !py-2 !px-5 text-sm"
              data-testid="purchase-continue"
              title={needsProject ? L("Select a project first", "Sélectionnez d'abord un projet") : L("Continue to payment", "Continuer vers le paiement")}
            >
              {L("Continue to payment", "Continuer vers le paiement")}
              <ArrowRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};
